import React, { useEffect, useState, useMemo, useContext, useCallback, useRef } from 'react';
import { Button, Container, Table, Spinner, Alert, Modal, Form, Row, Col, InputGroup, Card, ButtonGroup, Dropdown, Collapse, Badge } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { components as selectComponents } from 'react-select';
import PhoneInput from 'react-phone-number-input';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { BsWhatsapp, BsClipboardCheck, BsBoxArrowUpRight, BsShareFill } from 'react-icons/bs';
import { FaMoneyBillWave, FaCreditCard, FaExchangeAlt, FaCheck, FaTimes, FaPrint, FaWhatsapp, FaEnvelope, FaTrash, FaExclamationTriangle, FaInfoCircle, FaEdit, FaShareAlt, FaPlus, FaEye, FaEyeSlash, FaArrowLeft, FaCashRegister, FaTable, FaTh, FaMotorcycle, FaFileAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import api, { getProducts, getClients, getSuppliers, createClient, updateClient, getExpenses, getSaleById, createProduct } from '../services/api';
import { trackAction } from '../services/analytics';
import Pagination from '../components/Pagination';
import PageSizeSelector from '../components/PageSizeSelector';
import AuthContext from '../context/AuthContext';
import { handlePhoneBlurLogic } from '../utils/phoneUtils';
import CreateExpenseModal from '../components/CreateExpenseModal';
import useIsMobile from '../hooks/useIsMobile';
import ShareModal from '../components/ShareModal';
import CurrencyInput from '../components/CurrencyInput';
import PaymentMethodsSelector from '../components/PaymentMethodsSelector';
import PaymentMethodIndicator from '../components/PaymentMethodIndicator';
import NumberInput from '../components/NumberInput';
import useCashRegisterAutoOpen from '../hooks/useCashRegisterAutoOpen';
import useCashRegisterStatus from '../hooks/useCashRegisterStatus';
import OpenCashRegisterModal from '../components/OpenCashRegisterModal';
import CloseCashRegisterModal from '../components/CloseCashRegisterModal';
import CheckoutModal from '../components/CheckoutModal';
import FinancialSummaryCard from '../components/FinancialSummaryCard';


const ReadOnlyInput = ({ value, ...rest }) => (
    <Form.Control {...rest} value={value} readOnly />
);

// Fixes paste on mobile devices for react-select (iOS/Android)
const MobilePasteInput = (props) => (
    <selectComponents.Input
        {...props}
        onPaste={(e) => {
            const pastedText = e.clipboardData.getData('text/plain');
            if (pastedText && props.selectProps?.onInputChange) {
                props.selectProps.onInputChange(pastedText, { action: 'input-change' });
            }
            e.preventDefault();
        }}
    />
);

const isPhoneNumber = (str) => str && str.match(/[0-9]/g)?.length >= 5;

const SalesPage = () => {
    const { user, business } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t, i18n } = useTranslation();
    const isMobile = useIsMobile();

    const isSubscriptionActive = ['active', 'trialing'].includes(business?.subscription_status);

    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [productList, setProductList] = useState([]);
    const [clientList, setClientList] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({ client_name: '', client_phone: '', client_phone_country_code: '', client_address: '', products: [] });
    const [editingSale, setEditingSale] = useState(null);
    const [modalError, setModalError] = useState('');
    const [initialPayment, setInitialPayment] = useState('');
    const [paymentMethods, setPaymentMethods] = useState([{ method: 'Cash', amount: '' }]);
    const [productInputValues, setProductInputValues] = useState({});
    const justSelectedRef = useRef({});
    const [currentStep, setCurrentStep] = useState(1); // Wizard Step State
    const [isLayaway, setIsLayaway] = useState(false); // Sale type: false = Direct, true = Layaway
    const [saveClientData, setSaveClientData] = useState(false); // Checkbox to save client
    const [existingClientData, setExistingClientData] = useState(null); // Existing client data if phone matches
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false); // Show update confirmation modal
    const [hasMultiplePayments, setHasMultiplePayments] = useState(false); // Track if sale has multiple payments (disables payment editing)
    const [pendingSalesCount, setPendingSalesCount] = useState(0);


    const getLocalISODate = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getNextHourDateTime = () => {
        const now = new Date();
        const nextHour = new Date(now);
        nextHour.setHours(now.getHours() + 1);
        nextHour.setMinutes(0);
        nextHour.setSeconds(0);
        nextHour.setMilliseconds(0);

        const year = nextHour.getFullYear();
        const month = (nextHour.getMonth() + 1).toString().padStart(2, '0');
        const day = nextHour.getDate().toString().padStart(2, '0');
        const hours = nextHour.getHours().toString().padStart(2, '0');
        const minutes = nextHour.getMinutes().toString().padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const getCurrentDateTime = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };


    const location = useLocation();
    const today = getLocalISODate();

    // Initialize filters by reading URL params first to avoid duplicate requests
    const getInitialFilters = () => {
        const query = new URLSearchParams(location.search);
        const statusParam = query.get('status');

        if (statusParam === 'OVERDUE') {
            return { client: '', status: 'OVERDUE', startDate: '', endDate: '' };
        }
        return { client: '', status: '', startDate: today, endDate: today };
    };

    const getInitialDateFilterType = () => {
        const query = new URLSearchParams(location.search);
        const statusParam = query.get('status');
        return statusParam === 'OVERDUE' ? 'deliveryDate' : 'saleDate';
    };

    const [filters, setFilters] = useState(getInitialFilters);
    const [activeFilters, setActiveFilters] = useState(getInitialFilters);
    const [dateFilterType, setDateFilterType] = useState(getInitialDateFilterType);

    const [showShareModal, setShowShareModal] = useState(false);
    const [shareableLink, setShareableLink] = useState('');
    const [saleToShare, setSaleToShare] = useState(null);
    const [receiptForDownload, setReceiptForDownload] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalSalesAmount, setTotalSalesAmount] = useState(0);
    const [totalCollectedAmount, setTotalCollectedAmount] = useState(0);
    const [totalExpensesAmount, setTotalExpensesAmount] = useState(0);
    const [showAmounts, setShowAmounts] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('salesViewMode') || 'table');

    const toggleViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('salesViewMode', mode);
    };

    // State for the Add Payment Modal
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [saleForPayment, setSaleForPayment] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentError, setPaymentError] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [paymentObservations, setPaymentObservations] = useState('');
    const [paymentCashReceived, setPaymentCashReceived] = useState('');
    const [addPaymentMethods, setAddPaymentMethods] = useState([{ method: 'Cash', amount: '' }]);

    const [isLastInstallment, setIsLastInstallment] = useState(false);
    const [isInstallmentLimitReached, setIsInstallmentLimitReached] = useState(false);

    // Sale detail modal
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailSale, setDetailSale] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [currentInstallmentNumber, setCurrentInstallmentNumber] = useState(0);

    // Expense Modal States
    const [suppliers, setSuppliers] = useState([]);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [showClientDetails, setShowClientDetails] = useState(false);

    // Cash Register States - using reusable hook for auto-open flow
    const { isOpen: isCashRegisterOpen, loading: cashLoading, refetch: refetchCashStatus, cashRegister } = useCashRegisterStatus();
    const [showCloseCashModal, setShowCloseCashModal] = useState(false);

    // Cashier checkout of PENDING_CASHIER orders
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [saleToCheckout, setSaleToCheckout] = useState(null);
    // Cajas Fase 1 (estaciones responsables + enrutamiento PENDING_CASHIER, tarea 005) is gated by
    // the `cash_register_stations` plan feature. When off, its UI is hidden (backend also gates it).
    const cashStationsEnabled = !!business?.plan_features?.cash_register_stations;

    // Auto-open hook for transactions
    const hasExistingTransactions = sales.length > 0 || totalExpensesAmount > 0;
    const {
        checkAndPromptOpen,
        setPendingTransactionCallback,
        openModal: openCashRegisterModal,
        openContext,
        canOpenAStation,
        modalProps: cashModalProps
    } = useCashRegisterAutoOpen({
        hasExistingTransactions,
        hasPermission: hasPermission(PERMISSIONS.VIEW_CASH_REGISTER)
    });

    const handleAddPaymentClick = (sale) => {
        const remaining = parseFloat(sale.total_amount) - parseFloat(sale.paid_amount);
        setSaleForPayment(sale);

        // Calculate installment info
        const paymentsCount = sale.payments ? sale.payments.length : 0;
        const nextInstallment = paymentsCount + 1;
        setCurrentInstallmentNumber(nextInstallment);

        const limitReached = maxInstallments !== -1 && nextInstallment > maxInstallments;
        const lastInstallment = maxInstallments !== -1 && nextInstallment === maxInstallments;

        setIsInstallmentLimitReached(limitReached);
        setIsLastInstallment(lastInstallment);

        if (lastInstallment) {
            setPaymentAmount(remaining.toFixed(2)); // Force full amount
        } else {
            setPaymentAmount(remaining.toFixed(2)); // Pre-fill but editable
        }

        setPaymentError('');
        setPaymentMethod('Cash');
        setPaymentObservations('');
        setPaymentCashReceived('');
        setAddPaymentMethods([{ method: 'Cash', amount: remaining.toFixed(2) }]);
        setShowPaymentModal(true);
    };

    const handlePaymentModalClose = () => {
        setShowPaymentModal(false);
        setSaleForPayment(null);
    };

    const handlePaymentSubmit = async () => {
        // Calculate total from multiple methods
        const totalFromMethods = addPaymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
        const amount = totalFromMethods > 0 ? totalFromMethods : parseFloat(paymentAmount);

        if (!amount || amount <= 0) {
            setPaymentError(t('salesPage.paymentModal.validationError'));
            return;
        }

        /*
        if (paymentMethod === 'Cash') {
            const cashReceived = parseFloat(paymentCashReceived);
            if (!cashReceived || cashReceived < amount) {
                setPaymentError(t('salesPage.paymentModal.cashReceivedError'));
                return;
            }
        }
        */

        // Validate that payment amount does not exceed remaining balance
        const remaining = parseFloat(saleForPayment.total_amount) - parseFloat(saleForPayment.paid_amount);
        // Allow a small epsilon for float comparison issues
        if (amount > remaining + 0.01) {
            setPaymentError(t('salesPage.paymentModal.amountExceedsRemaining') || 'El monto excede el saldo restante');
            return;
        }

        try {
            // Filter out empty amounts
            const validBreakdown = addPaymentMethods.filter(pm => parseFloat(pm.amount) > 0);

            const submissionData = {
                amount,
                payment_method: validBreakdown.length === 1 ? validBreakdown[0].method : 'Multiple',
                observations: paymentObservations,
                payments_breakdown: validBreakdown
            };
            const response = await api.post(`/sales/${saleForPayment.sale_id}/payments`, submissionData);
            setSales(sales.map(s => s.sale_id === saleForPayment.sale_id ? response.data : s));
            trackAction('sale_payment_added', { payment_methods_count: validBreakdown.length });
            toast.success(t('paymentAddedSuccess'));
            handlePaymentModalClose();
            // Refetch to update totals
            const params = new URLSearchParams({ ...activeFilters, page: currentPage, limit: pageSize, dateFilterType });
            const res = await api.get(`/sales?${params}`);
            setTotalSalesAmount(res.data.totalSalesAmount);
            setTotalCollectedAmount(res.data.totalCollectedAmount);
        } catch (error) {
            setPaymentError(error.response?.data?.message || t('failedToAddPayment'));
        }
    };


    const [maxInstallments, setMaxInstallments] = useState(-1);
    const [isRecurringTemplate, setIsRecurringTemplate] = useState(false);
    const [recurringEnabled, setRecurringEnabled] = useState(false);
    const [repeatSourceSaleId, setRepeatSourceSaleId] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [productsRes, clientsRes, suppliersRes, planRes] = await Promise.allSettled([
                    getProducts({ pagination: false }),
                    getClients({ pagination: false }),
                    getSuppliers({ pagination: false }),
                    api.get('/plans/current')
                ]);
                if (productsRes.status === 'fulfilled') setProductList(productsRes.value.data);
                if (clientsRes.status === 'fulfilled') setClientList(clientsRes.value.data);
                if (suppliersRes.status === 'fulfilled') setSuppliers(suppliersRes.value.data);

                // Parse max_installments from plan features
                if (planRes.status === 'fulfilled') {
                    const plan = planRes.value.data;
                    let installments = -1;
                    if (plan.features && typeof plan.features === 'object') {
                        installments = plan.features.max_installments !== undefined ? plan.features.max_installments : -1;
                        setRecurringEnabled(!!plan.features.recurring_sales);
                    }
                    setMaxInstallments(installments);
                }

            } catch (error) {
                console.error("Failed to fetch initial data", error);
                setError(t('failedToLoadInitial'));
            }
        };
        fetchData();
    }, [t]);

    const productOptions = useMemo(() => productList.map(p => ({ value: p.name, label: p.name, ...p })), [productList]);
    const clientOptions = useMemo(() => clientList.map(c => ({ value: c.client_id, label: `${c.name} (${c.phone || 'No phone'})`, ...c })), [clientList]);

    // react-select no fuerza min-width:0 en sus contenedores flex por defecto, así que un
    // valor largo (nombre + teléfono) se desborda de la caja en vez de truncarse con "…".
    const selectTruncateStyles = {
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        control: base => ({ ...base, minWidth: 0 }),
        valueContainer: base => ({ ...base, minWidth: 0, flexWrap: 'nowrap' }),
        singleValue: base => ({ ...base, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }),
    };

    // Sin esto, el <CreatableSelect> de cliente queda visualmente vacío al editar una venta
    // con client_id ya asignado (no tiene `value`, solo `onChange`). Fallback a los datos de
    // la venta si el cliente aún no cargó en clientOptions.
    const selectedClientOption = useMemo(() => {
        if (!modalData.client_id) return null;
        return clientOptions.find(o => o.value === modalData.client_id)
            || { value: modalData.client_id, label: `${modalData.client_name || ''} (${modalData.client_phone || 'No phone'})` };
    }, [modalData.client_id, modalData.client_name, modalData.client_phone, clientOptions]);

    const formatCurrency = useCallback((num) => {
        const parsed = parseFloat(num || 0);
        if (isNaN(parsed)) return '$0';
        try {
            return '$' + parsed.toLocaleString(i18n.language, { minimumFractionDigits: 0, maximumFractionDigits: 0, style: 'decimal' });
        } catch (e) {
            return '$' + parsed.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0, style: 'decimal' });
        }
    }, [i18n.language]);

    // Expense Modal Functions
    const handleCreateExpenseClick = () => {
        setShowExpenseModal(true);
    };

    const addProductItem = () => setModalData(prev => ({ ...prev, products: [...prev.products, { product_name: '', quantity: 1, unit_price: '', save_to_catalog: false }] }));
    const removeProductItem = (index) => setModalData(prev => ({ ...prev, products: prev.products.filter((_, i) => i !== index) }));

    const handleProductItemChange = (index, e) => {
        const { name, value } = e.target;
        const updatedProducts = [...modalData.products];
        updatedProducts[index] = { ...updatedProducts[index], [name]: value };
        setModalData(prev => ({ ...prev, products: updatedProducts }));
    };

    const handleToggleSaveProduct = (index, value) => {
        const updatedProducts = [...modalData.products];
        updatedProducts[index] = { ...updatedProducts[index], save_to_catalog: value };
        setModalData(prev => ({ ...prev, products: updatedProducts }));
    };

    const handleProductSelectChange = (index, selectedOption, actionMeta) => {
        if (selectedOption) {
            justSelectedRef.current[index] = true;
        }
        const updatedProducts = [...modalData.products];
        let productName = '';
        let unitPrice = updatedProducts[index].unit_price;

        if (selectedOption) {
            if (actionMeta.action === 'select-option') {
                productName = selectedOption.value;
                unitPrice = selectedOption.unit_price;
            } else if (actionMeta.action === 'create-option') {
                productName = selectedOption.value;
                unitPrice = '';
            }
        } else {
            // Handle clear action
            productName = '';
            unitPrice = '';
        }

        updatedProducts[index] = { ...updatedProducts[index], product_name: productName, unit_price: unitPrice, save_to_catalog: false };
        setModalData(prev => ({ ...prev, products: updatedProducts }));
    };

    const handleProductInputChange = (inputValue, index) => {
        setProductInputValues(prev => ({ ...prev, [index]: inputValue }));
    };

    const handleProductInputBlur = (index) => {
        if (isMobile) return;
        const inputValue = productInputValues[index];
        setTimeout(() => {
            if (justSelectedRef.current[index]) {
                justSelectedRef.current[index] = false;
                return;
            }
            const existingOption = productOptions.find(opt => opt.label === inputValue);
            const currentItem = modalData.products[index];

            if (inputValue && !existingOption && inputValue !== currentItem.product_name) {
                handleProductSelectChange(index, { value: inputValue, label: inputValue }, { action: 'create-option' });
            }
        }, 200);
    };

    const handleClientSearch = (selectedOption, actionMeta) => {
        if (actionMeta.action === 'select-option') {
            setShowClientDetails(false);
            setModalData(prev => ({
                ...prev,
                client_id: selectedOption.value,
                client_name: selectedOption.name,
                client_phone: selectedOption.phone || '',
                client_phone_country_code: selectedOption.phone_country_code || '+57',
                client_address: selectedOption.address || ''
            }));
        } else if (actionMeta.action === 'create-option') {
            setShowClientDetails(true);
            const newValue = selectedOption.value;
            if (isPhoneNumber(newValue)) {
                setModalData(prev => ({ ...prev, client_id: null, client_name: '', client_phone: newValue, client_phone_country_code: '+57', client_address: '' }));
            } else {
                setModalData(prev => ({ ...prev, client_id: null, client_name: newValue, client_phone: '', client_phone_country_code: '+57', client_address: '' }));
            }
        } else if (actionMeta.action === 'clear') {
            setShowClientDetails(false);
            setModalData(prev => ({ ...prev, client_id: null, client_name: '', client_phone: '', client_phone_country_code: '', client_address: '' }));
        }
    };

    const handleModalCountryCodeChange = (value) => {
        setModalData(prev => ({ ...prev, client_phone_country_code: value }));
    };

    const handlePhoneBlur = () => {
        // Existing phone formatting logic
        handlePhoneBlurLogic(
            modalData.client_phone,
            modalData.client_phone_country_code,
            (newCountry) => setModalData(prev => ({ ...prev, client_phone_country_code: newCountry })),
            (newPhone) => setModalData(prev => ({ ...prev, client_phone: newPhone }))
        );

        // New duplicate detection logic
        if (!saveClientData) return;

        const phone = modalData.client_phone?.trim();
        if (!phone) return;

        const existing = clientList.find(c => c.phone === phone);
        if (existing) {
            // Check if there are changes
            const hasChanges =
                existing.name !== modalData.client_name?.trim() ||
                existing.address !== modalData.client_address?.trim();

            if (hasChanges) {
                setExistingClientData(existing);
                setShowUpdateConfirm(true);
            }
        }
    };

    const handleUpdateClient = async () => {
        try {
            await updateClient(existingClientData.client_id, {
                name: modalData.client_name,
                phone: modalData.client_phone,
                phone_country_code: modalData.client_phone_country_code,
                address: modalData.client_address,
                email: existingClientData.email // Keep original email
            });

            setShowUpdateConfirm(false);
            setExistingClientData(null);
            toast.success(t('salesPage.saleModal.clientUpdated', 'Datos del cliente actualizados'));
        } catch (error) {
            console.error('Error updating client:', error);
            toast.error(t('salesPage.saleModal.updateFailed', 'Error al actualizar cliente'));
        }
    };

    const handleContinueWithoutUpdate = () => {
        setShowUpdateConfirm(false);
        setExistingClientData(null);
        // Continue with the entered data without updating the client in DB
    };


    useEffect(() => {
        const total = modalData.products?.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)), 0) || 0;
        setModalData(prev => ({ ...prev, total_amount: total }));
    }, [modalData.products]);

    const handleModalClose = () => {
        setShowModal(false);
        setEditingSale(null);
        setModalData({ client_name: '', client_phone: '', client_phone_country_code: '+57', client_address: '', products: [], total_amount: 0, payment_method: 'Cash', observations: '', delivery_datetime: '', sale_date: '', status: 'Pending' });
        setModalError('');
        setInitialPayment('');
        setPaymentMethods([{ method: 'Cash', amount: '' }]);

        setProductInputValues({});
        setCurrentStep(1);
        setShowClientDetails(false);
        setIsLayaway(false); // Reset to direct sale
        setSaveClientData(false); // Reset checkbox
        setExistingClientData(null); // Clear existing client data
        setShowUpdateConfirm(false); // Close confirmation modal
        setHasMultiplePayments(false); // Reset multiple payments flag
        setIsRecurringTemplate(false); // Reset recurring template flag
        setRepeatSourceSaleId(null); // Reset repeat source
    };

    const handleCreateClick = () => {
        setEditingSale(null);
        setModalData({
            client_name: '',
            client_phone: '',
            client_phone_country_code: '+57',
            client_address: '',
            products: [{ product_name: '', quantity: 1, unit_price: '', save_to_catalog: false }],
            total_amount: 0,
            payment_method: 'Cash',
            observations: '',
            sale_date: getCurrentDateTime(), // Default sale date to current time
            delivery_datetime: getNextHourDateTime(), // Set default delivery date/time
            status: 'Pending'
        });
        setInitialPayment('');
        setPaymentMethods([{ method: 'Cash', amount: '' }]);
        setShowModal(true);
        setCurrentStep(1);
        setShowClientDetails(false);
        setIsLayaway(false); // Default to direct sale
        setIsRecurringTemplate(false);
        setRepeatSourceSaleId(null);
    };

    // Handle "Repeat Sale" — pre-fill modal with data from an existing sale
    const handleRepeatSale = (sale) => {
        if (!sale || !sale.products || sale.products.length === 0) {
            toast.error(t('salesPage.recurring.noProducts', 'La venta no tiene productos para repetir'));
            return;
        }

        // Resolve current catalog prices for each product
        const prefilledProducts = sale.products.map(item => {
            const catalogProduct = productList.find(p => p.name === item.product_name);
            const currentPrice = catalogProduct ? parseFloat(catalogProduct.price) : parseFloat(item.unit_price);
            const hasStock = catalogProduct ? (catalogProduct.stock === null || catalogProduct.stock === undefined || catalogProduct.stock > 0) : null;
            return {
                product_name: item.product_name,
                quantity: parseInt(item.quantity, 10) || 1,
                unit_price: currentPrice,
                _no_stock: catalogProduct && hasStock === false,
                _not_in_catalog: !catalogProduct,
            };
        });

        setEditingSale(null);
        setModalData({
            client_name: sale.client_name || '',
            client_phone: sale.client_phone || '',
            client_phone_country_code: sale.client_phone_country_code || '+57',
            client_address: sale.client_address || '',
            client_id: sale.client_id || null,
            products: prefilledProducts,
            total_amount: prefilledProducts.reduce((sum, p) => sum + (p.quantity * p.unit_price), 0),
            payment_method: 'Cash',
            observations: '',
            sale_date: getCurrentDateTime(),
            delivery_datetime: getNextHourDateTime(),
            status: 'Pending'
        });
        setInitialPayment('');
        setPaymentMethods([{ method: 'Cash', amount: '' }]);
        setShowModal(true);
        setCurrentStep(1);
        setShowClientDetails(false);
        setIsLayaway(false);
        setIsRecurringTemplate(sale.is_recurring_template || false);
        setRepeatSourceSaleId(sale.sale_id);
        setShowDetailModal(false);

        // Show warnings for products not in catalog or without stock
        const warnings = prefilledProducts.filter(p => p._not_in_catalog || p._no_stock);
        if (warnings.length > 0) {
            const names = warnings.map(p => p.product_name).join(', ');
            toast(t('salesPage.recurring.stockWarning', 'Algunos productos pueden no estar en catálogo o sin stock: {{names}}', { names }), { icon: '⚠️', duration: 5000 });
        }
    };

    // Handle pre-fill from navigation state (e.g., from ClientsPage "Repetir")
    useEffect(() => {
        if (location.state?.repeatSale && productList.length > 0) {
            handleRepeatSale(location.state.repeatSale);
            // Clear the state to avoid re-triggering
            window.history.replaceState({}, document.title);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, productList]);

    // Auto-fill payment to 100% for direct sales
    useEffect(() => {
        const total = parseFloat(modalData.total_amount) || 0;
        if (!isLayaway && total > 0 && !editingSale) {
            setPaymentMethods([{ method: 'Cash', amount: total.toFixed(2) }]);
        }
    }, [isLayaway, modalData.total_amount, editingSale]);


    const toLocalISOString = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const offset = date.getTimezoneOffset() * 60000;
        const localDate = new Date(date.getTime() - offset);
        return localDate.toISOString().slice(0, 16);
    };

    const handleViewDetail = async (sale) => {
        setDetailSale(null);
        setShowDetailModal(true);
        setDetailLoading(true);
        try {
            const res = await getSaleById(sale.sale_id);
            setDetailSale(res.data);
        } catch (e) {
            console.error('Failed to load sale detail', e);
            toast.error(t('salesPage.detailModal.loadError', 'No se pudo cargar el detalle del pedido.'));
            setShowDetailModal(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleEditClick = (sale) => {
        setEditingSale(sale);
        setModalData({
            ...sale,
            products: sale.products || [],
            sale_date: toLocalISOString(sale.sale_date),
            delivery_datetime: toLocalISOString(sale.delivery_datetime),
            client_phone: sale.client_phone || '',
            client_phone_country_code: sale.client_phone_country_code || '+57',
            client_address: sale.client_address || '',
        });

        // Check if sale has multiple payments (disables payment editing)
        const payments = sale.payments || [];
        const multiplePayments = payments.length > 1;
        setHasMultiplePayments(multiplePayments);

        // Find the initial payment record (first payment by date, or marked as 'Initial payment')
        const initialPaymentRecord = payments.find(p => p.observations === 'Initial payment')
          || (payments.length > 0 ? payments[0] : null);

        if (initialPaymentRecord) {
            // Parse payment_details (may be string or already parsed object)
            let paymentDetailsArray = [];
            if (initialPaymentRecord.payment_details) {
                try {
                    const details = typeof initialPaymentRecord.payment_details === 'string'
                        ? JSON.parse(initialPaymentRecord.payment_details)
                        : initialPaymentRecord.payment_details;
                    paymentDetailsArray = Array.isArray(details) ? details : [];
                } catch (e) {
                    console.error('Error parsing payment_details:', e);
                }
            }

            // Map to component format: { method, amount }
            if (paymentDetailsArray.length > 0) {
                const mapped = paymentDetailsArray.map(p => ({
                    method: p.method,
                    amount: p.amount
                }));
                setPaymentMethods(mapped);
            } else {
                // Fallback: use the initial payment's method and amount
                setPaymentMethods([{
                    method: initialPaymentRecord.payment_method || 'Cash',
                    amount: initialPaymentRecord.amount || ''
                }]);
            }
            setInitialPayment(initialPaymentRecord.amount || '');
        } else {
            // No initial payment (original paid_amount was 0)
            setPaymentMethods([{ method: 'Cash', amount: '' }]);
            setInitialPayment('');
        }

        setShowModal(true);
        setCurrentStep(1);
        setShowClientDetails(!sale.client_id);
        setIsLayaway(sale.is_layaway ?? false); // Load sale type from existing sale
    };

    const handleShareClick = (sale) => {
        const link = `${window.location.origin}/receipt/${sale.public_id}`;
        setShareableLink(link);
        setSaleToShare(sale);
        setReceiptForDownload(null);
        setShowShareModal(true);

        // Full receipt payload (products, payments, business branding) for the
        // "Download as image" option — same source the public receipt page uses.
        api.get(`/public/sales/${sale.public_id}`)
            .then((res) => setReceiptForDownload((prev) => (
                res.data && res.data.invoice_number === sale.invoice_number ? res.data : prev
            )))
            .catch(() => { /* download option just won't show */ });
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareableLink);
        toast.success(t('toast.linkCopied'));
    };

    const handlePrintClick = (sale) => {
        const link = `${window.location.origin}/receipt/${sale.public_id}?print=true`;
        window.open(link, '_blank');
    };

    const handleDeliverySlipPrint = (sale) => {
        const link = `${window.location.origin}/delivery-slip/${sale.public_id}?print=true`;
        window.open(link, '_blank');
    };

    const handleDelete = async (saleId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('cantRevert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('yesDelete'),
            cancelButtonText: t('cancel')
        });
        if (result.isConfirmed) {
            try {
                await api.delete(`/sales/${saleId}`);
                fetchSales(); // Refresh all sales data including totals
                trackAction('sale_deleted');
                Swal.fire(t('deleted'), t('saleDeleted'), 'success');
            } catch (err) { setError(t('failedToDelete')); }
        }
    };

    const fetchSales = useCallback(async () => {
        try {
            setLoading(true);
            const filterParams = { ...activeFilters, page: currentPage, limit: pageSize, dateFilterType };
            // Only send recurring filter when true
            if (!filterParams.recurring) delete filterParams.recurring;
            const params = new URLSearchParams(filterParams);
            const response = await api.get(`/sales?${params}`);
            setSales(response.data.sales);
            setTotalPages(response.data.totalPages);
            setTotalSalesAmount(response.data.totalSalesAmount || 0);
            setTotalCollectedAmount(response.data.totalCollectedAmount || 0);

            // Fetch Expenses matching the same date filters
            if (hasPermission(PERMISSIONS.VIEW_EXPENSES)) {
                let expenseParams = { pagination: 'true', limit: 1 }; // Minimal fetch just for total
                // Map sales filters to expense filters
                if (filters.startDate) expenseParams.startDate = filters.startDate;
                if (filters.endDate) expenseParams.endDate = filters.endDate;

                try {
                    const expensesRes = await getExpenses(expenseParams);
                    setTotalExpensesAmount(expensesRes.data.totalAmount || 0);
                } catch (expErr) {
                    console.error("Failed to fetch expenses summary", expErr);
                    setTotalExpensesAmount(0);
                }
            }

            setError('');
        } catch (err) {
            setError(t('failedToFetch'));
        } finally {
            setLoading(false);
        }
    }, [activeFilters, currentPage, pageSize, dateFilterType, hasPermission, filters.startDate, filters.endDate, t]);

    useEffect(() => {
        if (hasPermission(PERMISSIONS.VIEW_SALES)) {
            fetchSales();
        } else {
            setLoading(false);
            setError(t('noPermissionView'));
        }
    }, [activeFilters, currentPage, pageSize, dateFilterType, hasPermission, t]);

    // ─── Offline queue helpers ────────────────────────────────────────────────
    const PENDING_QUEUE_KEY = `evazard_pending_sales_${user?.business_id || 'default'}`;

    const getPendingQueue = useCallback(() => {
        try { return JSON.parse(localStorage.getItem(PENDING_QUEUE_KEY) || '[]'); }
        catch { return []; }
    }, [PENDING_QUEUE_KEY]);

    const addToPendingQueue = useCallback((data) => {
        const queue = getPendingQueue();
        if (!queue.find(e => e.data.client_generated_id === data.client_generated_id)) {
            const updated = [...queue, { data, timestamp: Date.now() }];
            localStorage.setItem(PENDING_QUEUE_KEY, JSON.stringify(updated));
            setPendingSalesCount(updated.length);
        }
    }, [getPendingQueue, PENDING_QUEUE_KEY]);

    const removeFromPendingQueue = useCallback((clientGeneratedId) => {
        const updated = getPendingQueue().filter(e => e.data.client_generated_id !== clientGeneratedId);
        localStorage.setItem(PENDING_QUEUE_KEY, JSON.stringify(updated));
        setPendingSalesCount(updated.length);
    }, [getPendingQueue, PENDING_QUEUE_KEY]);

    const processPendingQueue = useCallback(async () => {
        if (!navigator.onLine) return;
        const queue = getPendingQueue();
        if (queue.length === 0) return;

        let synced = 0;
        for (const entry of queue) {
            try {
                const response = await api.post('/sales', entry.data);
                removeFromPendingQueue(entry.data.client_generated_id);
                setSales(prev => {
                    const exists = prev.some(s => s.sale_id === response.data.sale_id);
                    return exists ? prev : [response.data, ...prev];
                });
                synced++;
            } catch (e) {
                if (e.response) {
                    // Error permanente (no de red): remover del queue y notificar
                    removeFromPendingQueue(entry.data.client_generated_id);
                    toast.error(`${t('salesPage.offline.syncError', 'Error al sincronizar venta')}: ${e.response?.data?.message}`);
                }
                // Error de red: mantener en queue, se reintentará
            }
        }
        if (synced > 0) {
            toast.success(t('salesPage.offline.syncSuccess', `${synced} venta(s) sincronizada(s) exitosamente`));
            fetchSales();
        }
    }, [getPendingQueue, removeFromPendingQueue, fetchSales, t]);

    useEffect(() => {
        setPendingSalesCount(getPendingQueue().length);
        if (getPendingQueue().length > 0 && navigator.onLine) {
            processPendingQueue();
        }
        window.addEventListener('online', processPendingQueue);
        return () => window.removeEventListener('online', processPendingQueue);
    }, [getPendingQueue, processPendingQueue]);
    // ─────────────────────────────────────────────────────────────────────────

    const handleModalChange = (e) => setModalData({ ...modalData, [e.target.name]: e.target.value });
    const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
    const applyFilters = () => {
        setCurrentPage(1);
        setActiveFilters(filters);
    };
    const clearFilters = () => {
        const today = getLocalISODate();
        const initialFilters = { client: '', status: '', startDate: today, endDate: today };
        setFilters(initialFilters);
        setCurrentPage(1);
        setActiveFilters(initialFilters);
        setDateFilterType('saleDate');
    };

    const handleDateFilterTypeChange = (type) => {
        setDateFilterType(type);
        // El toggle "Recurrentes" deja `recurring: true` y las fechas vacías en `filters` (no
        // filtra por fecha). Sin este reset, ese estado se arrastraba silenciosamente al volver
        // a "Ventas del Día"/"Entregas del Día": seguían filtrando por plantillas recurrentes (o,
        // tras un segundo toggle, sin fecha ni filtro alguno, mostrando TODAS las ventas).
        // Igual con el quick-filter "Por Cobrar": volver a un tab de fecha lo suelta (pero
        // respeta un estado elegido a mano en el dropdown).
        const restoredFilters = {
            ...filters,
            recurring: false,
            status: filters.status === 'PENDING_CASHIER' ? '' : filters.status,
            startDate: filters.startDate || today,
            endDate: filters.endDate || today,
        };
        setFilters(restoredFilters);
        setCurrentPage(1);
        setActiveFilters(restoredFilters);
    }

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    // Function to actually save the sale (called directly or after cash register opens)
    const saveSale = async (submissionData, isRetry = false) => {
        // Guardar el cliente ANTES de crear la venta: así createSale (POST /sales) lo encuentra
        // por teléfono/nombre y enlaza client_id solo. Evita el PUT /sales/:id parcial que el
        // backend rechazaba con 400 (updateSale exige el payload completo), lo que mostraba el
        // toast "No se pudo guardar el cliente" aunque el cliente sí se había creado.
        let clientJustCreated = false;
        if (!isRetry && saveClientData && !editingSale && !submissionData.client_id && submissionData.client_phone?.trim()) {
            const phoneExists = clientList.find(c => c.phone === submissionData.client_phone);
            if (!phoneExists) {
                try {
                    const newClientRes = await createClient({
                        name: submissionData.client_name,
                        phone: submissionData.client_phone,
                        phone_country_code: submissionData.client_phone_country_code,
                        address: submissionData.client_address
                    });
                    setClientList(prev => [...prev, newClientRes.data]);
                    clientJustCreated = true;
                } catch (error) {
                    console.error('Error saving client:', error);
                    // No bloquea la venta: solo avisamos ante un error real de API. Si es un error
                    // de red (sin conexión), lo maneja la cola offline del catch externo sin toast.
                    if (error.response) {
                        toast.error(t('salesPage.saleModal.clientSaveFailed', 'No se pudo guardar el cliente'));
                    }
                }
            }
        }

        // Guardar productos nuevos marcados para registrar en el catálogo ANTES de crear la venta
        if (!isRetry && !editingSale && submissionData.products && submissionData.products.length > 0) {
            const newlyCreatedProducts = [];
            for (let i = 0; i < submissionData.products.length; i++) {
                const prodItem = submissionData.products[i];
                if (prodItem.save_to_catalog && prodItem.product_name?.trim()) {
                    const alreadyInCatalog = productList.some(p => p.name?.trim().toLowerCase() === prodItem.product_name.trim().toLowerCase());
                    if (!alreadyInCatalog) {
                        try {
                            const newProdRes = await createProduct({
                                name: prodItem.product_name.trim(),
                                unit_price: parseFloat(prodItem.unit_price) || 0,
                                cost_price: 0,
                                available_quantity: null,
                                catalog_visible: false
                            });
                            if (newProdRes.data) {
                                newlyCreatedProducts.push(newProdRes.data);
                                prodItem.product_id = newProdRes.data.product_id;
                            }
                        } catch (prodErr) {
                            console.error('Error saving new product to catalog:', prodErr);
                            if (prodErr.response) {
                                toast.error(t('salesPage.saleModal.productSaveFailed', 'No se pudo guardar el producto en el catálogo: {{name}}', { name: prodItem.product_name }));
                            }
                        }
                    }
                }
            }
            if (newlyCreatedProducts.length > 0) {
                setProductList(prev => [...prev, ...newlyCreatedProducts]);
            }
        }

        try {
            let response;
            if (editingSale) {
                response = await api.put(`/sales/${editingSale.sale_id}`, submissionData);
                setSales(sales.map(s => s.sale_id === editingSale.sale_id ? response.data : s));
            } else {
                response = await api.post('/sales', submissionData);
                setSales(prev => {
                    const exists = prev.some(s => s.sale_id === response.data.sale_id);
                    return exists ? prev : [response.data, ...prev];
                });
            }
            // Remove from offline queue on success
            if (submissionData.client_generated_id) {
                removeFromPendingQueue(submissionData.client_generated_id);
            }

            // Telemetría (tarea 004): acción clave sin PII (solo contadores/booleanos).
            trackAction('sale_saved', {
                is_edit: !!editingSale,
                is_retry: isRetry,
                items_count: submissionData.products?.length ?? 0,
                is_fully_paid: parseFloat(submissionData.paid_amount) >= parseFloat(submissionData.total_amount),
            });

            if (!isRetry) {
                handleModalClose();
                toast.success(t('saleSavedSuccess'));
                if (clientJustCreated) {
                    toast.success(t('salesPage.saleModal.clientSaved', 'Cliente guardado exitosamente'));
                }
            }

            // Refetch to update totals
            fetchSales();

            if (!isRetry) {
                // Only open share modal for new, fully paid sales
                if (!editingSale && parseFloat(submissionData.paid_amount) >= parseFloat(submissionData.total_amount)) {
                    handleShareClick(response.data);
                }
            }
        } catch (error) {
            const isNetworkError = !error.response;
            // Telemetría (tarea 004): "camino no feliz" de una acción clave.
            trackAction('sale_save_failed', { is_network_error: isNetworkError, is_retry: isRetry });
            if (isNetworkError && !isRetry) {
                // Sin conexión: guardar en cola offline y cerrar modal
                addToPendingQueue(submissionData);
                handleModalClose();
                toast(t('salesPage.offline.savedLocally', 'Sin conexión: venta guardada localmente'), { icon: '📶' });
            } else if (!isNetworkError) {
                // Error de la API (no de red)
                setModalError(error.response?.data?.message || t('failedToSave'));
            }
            // isRetry + error de red → permanecer en queue, se reintentará al volver la conexión
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        setModalError('');
        if (!modalData.products || modalData.products.length === 0) {
            setModalError(t('salesPage.saleModal.validationError'));
            return;
        }

        // Base data
        let submissionData = { ...modalData };

        // Calculate total paid from methods
        const totalPaidFromMethods = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
        // Filter valid
        const validBreakdown = paymentMethods.filter(pm => parseFloat(pm.amount) > 0);

        if (validBreakdown.length > 0) {
            submissionData.payments_breakdown = validBreakdown;
            submissionData.paid_amount = totalPaidFromMethods;
            submissionData.payment_method = validBreakdown.length === 1 ? validBreakdown[0].method : 'Multiple';
        } else {
            submissionData.paid_amount = 0;
        }

        // Convert local delivery time to UTC before sending
        if (submissionData.delivery_datetime) {
            const d = new Date(submissionData.delivery_datetime);
            if (!isNaN(d.getTime())) submissionData.delivery_datetime = d.toISOString();
        }
        // Convert local sale date to UTC before sending
        if (submissionData.sale_date) {
            const d = new Date(submissionData.sale_date);
            if (!isNaN(d.getTime())) submissionData.sale_date = d.toISOString();
        }

        // Add sale type
        submissionData.is_layaway = isLayaway;

        // Add recurring template flag and source sale
        if (isRecurringTemplate) {
            submissionData.is_recurring_template = true;
        }
        if (repeatSourceSaleId && !editingSale) {
            submissionData.generated_from_sale_id = repeatSourceSaleId;
        }

        // Generate idempotency key for new sales (prevents duplicates on retry)
        if (!editingSale) {
            submissionData.client_generated_id = crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }

        // Check if cash register needs to be opened (uses reusable hook)
        const shouldProceed = await checkAndPromptOpen(() => saveSale(submissionData), !!editingSale);
        if (!shouldProceed) {
            return; // User chose to open cash register - sale will be saved after
        }

        // Save directly
        await saveSale(submissionData);
    };

    const renderSalesRows = () => {
        if (sales.length === 0) return (<tr><td colSpan="7" className="text-center">No sales recorded yet.</td></tr>);
        return sales.map(sale => (
            <tr key={sale.sale_id}>
                <td>
                    {sale.sale_id}
                    {sale.is_recurring_template && <Badge bg="info" className="ms-1" style={{ fontSize: '0.65em' }}>{t('salesPage.recurring.templateBadge', 'Recurrente')}</Badge>}
                </td>
                <td>{sale.client_name}</td>
                <td>{formatCurrency(sale.total_amount)}</td>
                <td>{new Date(sale.created_at).toLocaleString()}</td>
                <td>{new Date(sale.delivery_datetime).toLocaleString()}</td>
                <td><span className={`badge bg-${sale.status === 'Pending' ? 'warning' : 'success'}`}>{sale.status}</span></td>
                <td>
                    <Button variant="info" size="sm" onClick={() => handleShareClick(sale)} className="me-2" title={t('salesPage.actions.share')} aria-label={t('salesPage.actions.share')}><FaShareAlt /></Button>
                    {hasPermission(PERMISSIONS.EDIT_SALES) && (
                        <Button variant="secondary" size="sm" onClick={() => handleEditClick(sale)} className="me-2" title={t('salesPage.actions.edit')} aria-label={t('salesPage.actions.edit')}><FaEdit /></Button>
                    )}
                    {hasPermission(PERMISSIONS.DELETE_SALES) && (
                        <Button variant="danger" size="sm" onClick={() => handleDelete(sale.sale_id)} className="me-2" title={t('salesPage.actions.delete')} aria-label={t('salesPage.actions.delete')}><FaTrash /></Button>
                    )}
                    {recurringEnabled && hasPermission(PERMISSIONS.CREATE_SALES) && (
                        <Button variant="outline-info" size="sm" onClick={() => handleRepeatSale(sale)} title={t('salesPage.recurring.repeatButton')} aria-label={t('salesPage.recurring.repeatButton')}><FaExchangeAlt /></Button>
                    )}
                </td>
            </tr>
        ));
    };

    const changeToGive = parseFloat(initialPayment) - parseFloat(modalData.total_amount);
    const paymentChange = parseFloat(paymentCashReceived) - parseFloat(paymentAmount);

    const isPaymentValid = (() => {
        // If we are in the "Add Payment" modal, we check the array of methods
        if (saleForPayment && addPaymentMethods && addPaymentMethods.length > 0) {
            let totalAmount = 0;
            for (const pm of addPaymentMethods) {
                const amount = parseFloat(pm.amount);
                if (!amount || amount <= 0) return false;
                totalAmount += amount;
            }
            return totalAmount > 0;
        }

        // Legacy check / Fallback (though mostly unused now for this modal)
        const amount = parseFloat(paymentAmount);
        if (!amount || amount <= 0) return false;
        if (paymentMethod === 'Cash') {
            const cashReceived = parseFloat(paymentCashReceived);
            return cashReceived && cashReceived >= amount;
        }
        return true;
    })();



    return (
        <>
            <Container className="mt-4">
                <Row className="align-items-center mb-4">
                    <Col md={4}>
                        <h2>{t('salesPage.title')}</h2>
                        <p className="text-muted mb-0">{t('salesPage.subtitle')}</p>
                    </Col>
                    <Col md={4}>
                        {hasPermission(PERMISSIONS.VIEW_FINANCIAL_SUMMARY) && (
                            <FinancialSummaryCard
                                totalSales={totalSalesAmount}
                                totalCollected={totalCollectedAmount}
                                totalExpenses={totalExpensesAmount}
                                formatCurrency={formatCurrency}
                            />
                        )}
                    </Col>
                    <Col md={4} className="text-end d-none d-md-block">
                        <div className="d-flex gap-2 justify-content-end">
                            {/* Cash Register Button */}
                            {hasPermission(PERMISSIONS.VIEW_CASH_REGISTER) && !cashLoading && (
                                isCashRegisterOpen ? (
                                    hasPermission(PERMISSIONS.CLOSE_CASH_REGISTER) && (
                                        <Button
                                            variant="dark"
                                            onClick={() => setShowCloseCashModal(true)}
                                        >
                                            <FaCashRegister className="me-2" />
                                            {t('cashRegisterPage.salesPageIntegration.closeCashRegister')}
                                        </Button>
                                    )
                                ) : (
                                    hasPermission(PERMISSIONS.OPEN_CASH_REGISTER) && canOpenAStation && (
                                        <Button
                                            variant="success"
                                            onClick={() => openCashRegisterModal()}
                                        >
                                            <FaCashRegister className="me-2" />
                                            {t('cashRegisterPage.salesPageIntegration.openCashRegister')}
                                        </Button>
                                    )
                                )
                            )}
                            {hasPermission(PERMISSIONS.CREATE_SALES) && (
                                <div title={!isSubscriptionActive ? t('subscriptionRequired') : ''} className="d-inline-block">
                                    <Button
                                        variant="primary"
                                        onClick={handleCreateClick}
                                        disabled={!isSubscriptionActive}
                                    >
                                        {t('salesPage.newSaleButton')}
                                    </Button>
                                </div>
                            )}
                            {hasPermission(PERMISSIONS.CREATE_EXPENSES) && (
                                <div title={!isSubscriptionActive ? t('subscriptionRequired') : !business?.plan_features?.expenses ? t('common.featureNotAvailable') : ''} className="d-inline-block">
                                    <Button
                                        variant="danger"
                                        onClick={handleCreateExpenseClick}
                                        disabled={!isSubscriptionActive || !business?.plan_features?.expenses}
                                    >
                                        {t('expensesPage.newExpenseButton')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Offline pending sales banner */}
                {pendingSalesCount > 0 && (
                    <Alert variant="warning" className="d-flex align-items-center justify-content-between mb-3">
                        <span>
                            <FaExclamationTriangle className="me-2" />
                            {t('salesPage.offline.pendingBanner', `${pendingSalesCount} venta(s) guardada(s) sin conexión`)}
                        </span>
                        {navigator.onLine && (
                            <Button size="sm" variant="outline-warning" onClick={processPendingQueue}>
                                {t('salesPage.offline.syncNow', 'Sincronizar ahora')}
                            </Button>
                        )}
                    </Alert>
                )}

                {/* Mobile Fixed Bottom Buttons */}
                <div className="d-md-none position-fixed bottom-0 start-0 end-0 px-3 pt-3 bg-white shadow-lg" style={{ zIndex: 1000, borderTop: '1px solid #dee2e6', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
                    <div className="d-flex gap-2">
                        {hasPermission(PERMISSIONS.CREATE_SALES) && (
                            <Button
                                variant="primary"
                                onClick={handleCreateClick}
                                disabled={!isSubscriptionActive}
                                className="flex-grow-1"
                            >
                                {t('salesPage.newSaleButton')}
                            </Button>
                        )}
                        {hasPermission(PERMISSIONS.CREATE_EXPENSES) && (
                            <Button
                                variant="danger"
                                onClick={handleCreateExpenseClick}
                                disabled={!isSubscriptionActive || !business?.plan_features?.expenses}
                                className="flex-grow-1"
                            >
                                {t('expensesPage.newExpenseButton')}
                            </Button>
                        )}
                    </div>
                </div>

                <div className="d-flex justify-content-center mb-3 align-items-center">
                    {/* Mobile Cash Register Button */}
                    <div className="d-md-none me-2">
                        {hasPermission(PERMISSIONS.VIEW_CASH_REGISTER) && !cashLoading && (
                            isCashRegisterOpen ? (
                                hasPermission(PERMISSIONS.CLOSE_CASH_REGISTER) && (
                                    <Button
                                        variant="dark"
                                        onClick={() => setShowCloseCashModal(true)}
                                    >
                                        <FaCashRegister className="me-2" />
                                        {t('cashRegisterPage.salesPageIntegration.closeCashRegister')}
                                    </Button>
                                )
                            ) : (
                                hasPermission(PERMISSIONS.OPEN_CASH_REGISTER) && canOpenAStation && (
                                    <Button
                                        variant="success"
                                        onClick={() => openCashRegisterModal()}
                                    >
                                        <FaCashRegister className="me-2" />
                                        {t('cashRegisterPage.salesPageIntegration.openCashRegister')}
                                    </Button>
                                )
                            )
                        )}
                    </div>
                    <ButtonGroup>
                        <Button variant={dateFilterType === 'saleDate' ? 'primary' : 'outline-primary'} onClick={() => handleDateFilterTypeChange('saleDate')}>
                            {t('salesPage.salesOfDay')}
                        </Button>
                        <Button variant={dateFilterType === 'deliveryDate' ? 'primary' : 'outline-primary'} onClick={() => handleDateFilterTypeChange('deliveryDate')}>
                            {t('salesPage.deliveriesForDay')}
                        </Button>
                    </ButtonGroup>
                    {recurringEnabled && (
                        <Button
                            variant={activeFilters.recurring ? 'info' : 'outline-info'}
                            size="sm"
                            className="ms-2"
                            onClick={() => {
                                const turningOn = !activeFilters.recurring;
                                // Al activar: las plantillas recurrentes no están acotadas por fecha, se
                                // limpia el rango. Al desactivar: se restaura el rango (o "hoy" si estaba
                                // vacío) — si no, quedaba sin fechas Y sin `recurring`, mostrando TODAS
                                // las ventas del negocio sin ningún filtro aplicado.
                                const newFilters = turningOn
                                    // Al activar Recurrentes, suelta el quick-filter "Por Cobrar" si estaba
                                    // puesto (son vistas mutuamente excluyentes); no toca un estado elegido
                                    // manualmente en el dropdown.
                                    ? { ...filters, recurring: true, status: filters.status === 'PENDING_CASHIER' ? '' : filters.status, startDate: '', endDate: '' }
                                    : { ...filters, recurring: false, startDate: filters.startDate || today, endDate: filters.endDate || today };
                                setFilters(newFilters);
                                setActiveFilters(newFilters);
                                setCurrentPage(1);
                            }}
                        >
                            {t('salesPage.recurring.filterButton', 'Recurrentes')}
                        </Button>
                    )}
                    {cashStationsEnabled && hasPermission(PERMISSIONS.VIEW_CASH_REGISTER) && (
                        <Button
                            variant={activeFilters.status === 'PENDING_CASHIER' ? 'info' : 'outline-info'}
                            size="sm"
                            className="ms-2"
                            onClick={() => {
                                // Toggle a quick filter to the orders awaiting a cashier. Unlike the
                                // "Recurrentes" toggle we keep the date range (defaulting to "today" if it
                                // was empty) so the balance card stays scoped to the period instead of
                                // showing all-time totals; we also clear `recurring` so both toggles don't
                                // contaminate each other. Turning it off only drops the status filter.
                                const turningOn = activeFilters.status !== 'PENDING_CASHIER';
                                const newFilters = turningOn
                                    ? { ...filters, status: 'PENDING_CASHIER', recurring: false, startDate: filters.startDate || today, endDate: filters.endDate || today }
                                    : { ...filters, status: '' };
                                setFilters(newFilters);
                                setActiveFilters(newFilters);
                                setCurrentPage(1);
                            }}
                        >
                            <FaCashRegister className="me-1" aria-hidden="true" />
                            {t('salesPage.collectPending')}
                        </Button>
                    )}
                </div>

                <div className="d-md-none mb-3">
                    <Button variant="link" onClick={() => setShowFilters(!showFilters)} className="p-0 text-decoration-none">
                        {showFilters ? t('salesPage.filters.hideFilters', 'Ocultar filtros') : t('salesPage.filters.viewFilters', 'Ver filtros')}
                    </Button>
                </div>

                <Collapse in={showFilters || window.innerWidth >= 768}>
                    <div id="sales-filters">
                        <Card className="mb-4">
                            <Card.Body>
                                <Form>
                                    <Row className="align-items-end">
                                        <Col md={3}><Form.Group className="mb-2 mb-md-0"><Form.Label>{t('salesPage.filters.clientName')}</Form.Label><Form.Control type="text" name="client" value={filters.client} onChange={handleFilterChange} /></Form.Group></Col>
                                        <Col md={2}><Form.Group className="mb-2 mb-md-0"><Form.Label>{t('salesPage.filters.status')}</Form.Label><Form.Select name="status" value={filters.status} onChange={handleFilterChange}><option value="">{t('salesPage.filters.all')}</option>{cashStationsEnabled && <option value="PENDING_CASHIER">{t('salesPage.filters.pendingCashier', 'Por Cobrar (Cajero)')}</option>}<option value="PENDING_PAYMENT">{t('salesPage.filters.pendingPayment')}</option><option value="COMPLETED">{t('salesPage.filters.completed')}</option><option>{t('salesPage.filters.cancelled')}</option></Form.Select></Form.Group></Col>
                                        <Col md={2}><Form.Group className="mb-2 mb-md-0"><Form.Label>{t('salesPage.filters.startDate')}</Form.Label><Form.Control type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} /></Form.Group></Col>
                                        <Col md={2}><Form.Group className="mb-2 mb-md-0"><Form.Label>{t('salesPage.filters.endDate')}</Form.Label><Form.Control type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} /></Form.Group></Col>
                                        <Col md={3} className="d-flex mt-2 mt-md-0">
                                            <Button variant="primary" onClick={applyFilters} className="me-2 flex-grow-1 flex-md-grow-0">{t('salesPage.filters.apply')}</Button>
                                            <Button variant="secondary" onClick={clearFilters} className="flex-grow-1 flex-md-grow-0">{t('salesPage.filters.clear')}</Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>
                    </div>
                </Collapse>

                {activeFilters.status === 'OVERDUE' && (
                    <Alert variant="warning" className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>⚠️ {t('salesPage.overdueFilter', 'Mostrando ventas con pagos vencidos')}</strong>
                                <p className="mb-0 small">{t('salesPage.overdueDescription', 'Ventas con saldo pendiente cuya fecha de entrega ya pasó.')}</p>
                            </div>
                            <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => {
                                    setFilters({ client: '', status: '', startDate: getLocalISODate(), endDate: getLocalISODate() });
                                    setActiveFilters({ client: '', status: '', startDate: getLocalISODate(), endDate: getLocalISODate() });
                                    setDateFilterType('saleDate');
                                    window.history.pushState({}, '', '/sales');
                                }}
                            >
                                {t('common.clearFilter', 'Limpiar filtro')}
                            </Button>
                        </div>
                    </Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                {/* View Mode Toggle */}
                <div className="d-flex justify-content-end align-items-center mb-3">
                    <div
                        className="sales-view-toggle"
                        role="group"
                        aria-label={t('salesPage.viewToggle.label', 'Cambiar vista')}
                    >
                        <button
                            id="sales-view-table"
                            className={`sales-view-toggle__btn${viewMode === 'table' ? ' sales-view-toggle__btn--active' : ''}`}
                            onClick={() => toggleViewMode('table')}
                            aria-pressed={viewMode === 'table'}
                            title={t('salesPage.viewToggle.tableView', 'Vista tabla')}
                            aria-label={t('salesPage.viewToggle.tableView', 'Vista tabla')}
                        >
                            <FaTable aria-hidden="true" />
                            <span className="sales-view-toggle__label">{t('salesPage.viewToggle.tableView', 'Tabla')}</span>
                        </button>
                        <button
                            id="sales-view-cards"
                            className={`sales-view-toggle__btn${viewMode === 'cards' ? ' sales-view-toggle__btn--active' : ''}`}
                            onClick={() => toggleViewMode('cards')}
                            aria-pressed={viewMode === 'cards'}
                            title={t('salesPage.viewToggle.cardsView', 'Vista tarjetas')}
                            aria-label={t('salesPage.viewToggle.cardsView', 'Vista tarjetas')}
                        >
                            <FaTh aria-hidden="true" />
                            <span className="sales-view-toggle__label">{t('salesPage.viewToggle.cardsView', 'Tarjetas')}</span>
                        </button>
                    </div>
                </div>

                {loading ? <div className="text-center"><Spinner animation="border" /></div> :
                    viewMode === 'table' ? (
                        <div className="modern-table-wrapper mt-1">
                            <Table hover responsive className="modern-table">
                                <thead>
                                    <tr>
                                        <th>{t('salesPage.table.invoice')}</th>
                                        <th>{t('salesPage.table.client')}</th>
                                        <th>{t('salesPage.table.saleDate')}</th>
                                        <th>{t('salesPage.table.deliveryDate')}</th>
                                        <th>{t('salesPage.table.total')}</th>
                                        <th>{t('salesPage.table.paid')}</th>
                                        <th>{t('salesPage.table.remaining')}</th>
                                        <th>{t('salesPage.table.status')}</th>
                                        <th>{t('salesPage.table.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.length === 0
                                        ? <tr><td colSpan="9" className="text-center py-4 text-muted">{t('salesPage.table.noSales')}</td></tr>
                                        : sales.map(sale => {
                                            const remaining = parseFloat(sale.total_amount) - parseFloat(sale.paid_amount);
                                            const isPending = sale.status === 'PENDING_PAYMENT' && remaining > 0;
                                            const isPendingCashier = sale.status === 'PENDING_CASHIER';
                                            const badgeBg = isPendingCashier ? 'info' : isPending ? 'warning' : 'success';
                                            return (
                                                <tr key={sale.sale_id} className={isPendingCashier ? 'table-info' : isPending ? 'table-warning' : ''}>
                                                    <td>{sale.invoice_number}</td>
                                                    <td>{sale.client_name}</td>
                                                    <td>{new Date(sale.created_at).toLocaleString()}</td>
                                                    <td>{new Date(sale.delivery_datetime).toLocaleString()}</td>
                                                    <td>{formatCurrency(sale.total_amount)}</td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <PaymentMethodIndicator
                                                                paymentMethod={sale.payment_method}
                                                                payments={sale.payments || []}
                                                                t={t}
                                                            />
                                                            <span>{formatCurrency(sale.paid_amount)}</span>
                                                        </div>
                                                    </td>
                                                    <td className={remaining > 0 ? 'text-danger' : 'text-success'}>
                                                        {formatCurrency(remaining)}
                                                    </td>
                                                    <td><span className={`badge bg-${badgeBg}`}>{t(`salesPage.statuses.${sale.status}`, sale.status)}</span></td>
                                                    <td>
                                                        <div className="btn-actions">
                                                            <Button variant="outline-dark" size="sm" onClick={() => handleViewDetail(sale)} title={t('salesPage.actions.viewDetail', 'Ver detalle')}>
                                                                <FaEye />
                                                            </Button>
                                                            <Button variant="info" size="sm" onClick={() => handleShareClick(sale)} title={t('salesPage.actions.share')}>
                                                                <FaShareAlt />
                                                            </Button>
                                                            <Dropdown as={ButtonGroup}>
                                                                <Dropdown.Toggle variant="outline-secondary" size="sm" title={t('salesPage.actions.print', 'Imprimir')} id={`print-dropdown-${sale.sale_id}`}>
                                                                    <FaPrint />
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu>
                                                                    <Dropdown.Item onClick={() => handlePrintClick(sale)}>
                                                                        <FaFileAlt className="me-2" />{t('salesPage.actions.printReceipt', 'Recibo Completo')}
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item onClick={() => handleDeliverySlipPrint(sale)}>
                                                                        <FaMotorcycle className="me-2" />{t('salesPage.actions.printDeliverySlip', 'Ticket Domiciliario')}
                                                                    </Dropdown.Item>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                            {hasPermission(PERMISSIONS.EDIT_SALES) && (
                                                                <Button
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    onClick={() => handleEditClick(sale)}
                                                                    title={t('salesPage.actions.edit')}
                                                                    aria-label={t('salesPage.actions.edit')}
                                                                >
                                                                    <FaEdit />
                                                                </Button>
                                                            )}
                                                            {hasPermission(PERMISSIONS.DELETE_SALES) && (
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => handleDelete(sale.sale_id)}
                                                                    title={t('salesPage.actions.delete')}
                                                                    aria-label={t('salesPage.actions.delete')}
                                                                >
                                                                    <FaTrash />
                                                                </Button>
                                                            )}
                                                            {isPending && hasPermission(PERMISSIONS.CREATE_SALES) && (
                                                                <Button
                                                                    variant="success"
                                                                    size="sm"
                                                                    onClick={() => handleAddPaymentClick(sale)}
                                                                    title={t('salesPage.actions.addPayment')}
                                                                    aria-label={t('salesPage.actions.addPayment')}
                                                                >
                                                                    <FaPlus />
                                                                </Button>
                                                            )}
                                                            {cashStationsEnabled && isPendingCashier && hasPermission(PERMISSIONS.CREATE_SALES) && (
                                                                <Button
                                                                    variant="success"
                                                                    size="sm"
                                                                    onClick={() => { setSaleToCheckout(sale); setShowCheckoutModal(true); }}
                                                                    title={t('salesPage.actions.collect')}
                                                                    aria-label={t('salesPage.actions.collect')}
                                                                >
                                                                    <FaCashRegister />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    }
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        /* ── Cards view ── */
                        <div>
                            {sales.length === 0 ? (
                                <div className="text-center py-5 text-muted">{t('salesPage.table.noSales')}</div>
                            ) : (
                                <div className="sales-cards-grid">
                                    {sales.map(sale => {
                                        const remaining = parseFloat(sale.total_amount) - parseFloat(sale.paid_amount);
                                        const isPending = sale.status === 'PENDING_PAYMENT' && remaining > 0;
                                        const isPendingCashier = sale.status === 'PENDING_CASHIER';
                                        const badgeBg = isPendingCashier ? 'info' : isPending ? 'warning' : 'success';
                                        return (
                                            <div
                                                key={sale.sale_id}
                                                className={`sale-card${isPendingCashier ? ' sale-card--pending-cashier' : isPending ? ' sale-card--pending' : ''}`}
                                            >
                                                {/* Card header */}
                                                <div className="sale-card__header">
                                                    <div className="sale-card__invoice">
                                                        <span className="sale-card__invoice-label">{t('salesPage.table.invoice')}{sale.invoice_number}</span>
                                                        {sale.is_recurring_template && <Badge bg="info" className="ms-2">{t('salesPage.recurring.templateBadge', 'Recurrente')}</Badge>}
                                                    </div>
                                                    <span className={`badge bg-${badgeBg} sale-card__badge`}>
                                                        {t(`salesPage.statuses.${sale.status}`, sale.status)}
                                                    </span>
                                                </div>

                                                {/* Client */}
                                                <div className="sale-card__client">
                                                    <span className="sale-card__client-name">{sale.client_name || '—'}</span>
                                                </div>

                                                {/* Amounts */}
                                                <div className="sale-card__amounts">
                                                    <div className="sale-card__amount-item">
                                                        <span className="sale-card__amount-label">{t('salesPage.table.total')}</span>
                                                        <span className="sale-card__amount-value sale-card__amount-value--total">{formatCurrency(sale.total_amount)}</span>
                                                    </div>
                                                    <div className="sale-card__amount-item">
                                                        <span className="sale-card__amount-label">{t('salesPage.table.paid')}</span>
                                                        <span className="sale-card__amount-value sale-card__amount-value--paid">{formatCurrency(sale.paid_amount)}</span>
                                                    </div>
                                                    <div className="sale-card__amount-item">
                                                        <span className="sale-card__amount-label">{t('salesPage.table.remaining')}</span>
                                                        <span className={`sale-card__amount-value ${remaining > 0 ? 'sale-card__amount-value--due' : 'sale-card__amount-value--paid'}`}>{formatCurrency(remaining)}</span>
                                                    </div>
                                                </div>

                                                {/* Dates */}
                                                <div className="sale-card__dates">
                                                    <div className="sale-card__date-item">
                                                        <span className="sale-card__date-label">{t('salesPage.table.saleDate')}</span>
                                                        <span className="sale-card__date-value">{new Date(sale.created_at).toLocaleString()}</span>
                                                    </div>
                                                    <div className="sale-card__date-item">
                                                        <span className="sale-card__date-label">{t('salesPage.table.deliveryDate')}</span>
                                                        <span className="sale-card__date-value">{new Date(sale.delivery_datetime).toLocaleString()}</span>
                                                    </div>
                                                </div>

                                                {/* Actions */}
                                                <div className="sale-card__actions btn-actions">
                                                    <Button variant="outline-dark" size="sm" onClick={() => handleViewDetail(sale)} title={t('salesPage.actions.viewDetail', 'Ver detalle')} aria-label={t('salesPage.actions.viewDetail', 'Ver detalle')}>
                                                        <FaEye />
                                                    </Button>
                                                    <Button variant="info" size="sm" onClick={() => handleShareClick(sale)} title={t('salesPage.actions.share')} aria-label={t('salesPage.actions.share')}>
                                                        <FaShareAlt />
                                                    </Button>
                                                    <Dropdown as={ButtonGroup}>
                                                        <Dropdown.Toggle variant="outline-secondary" size="sm" title={t('salesPage.actions.print', 'Imprimir')} aria-label={t('salesPage.actions.print', 'Imprimir')} id={`print-dropdown-card-${sale.sale_id}`}>
                                                            <FaPrint />
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item onClick={() => handlePrintClick(sale)}>
                                                                <FaFileAlt className="me-2" />{t('salesPage.actions.printReceipt', 'Recibo Completo')}
                                                            </Dropdown.Item>
                                                            <Dropdown.Item onClick={() => handleDeliverySlipPrint(sale)}>
                                                                <FaMotorcycle className="me-2" />{t('salesPage.actions.printDeliverySlip', 'Ticket Domiciliario')}
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                    {hasPermission(PERMISSIONS.EDIT_SALES) && (
                                                        <Button variant="secondary" size="sm" onClick={() => handleEditClick(sale)} title={t('salesPage.actions.edit')} aria-label={t('salesPage.actions.edit')}>
                                                            <FaEdit />
                                                        </Button>
                                                    )}
                                                    {hasPermission(PERMISSIONS.DELETE_SALES) && (
                                                        <Button variant="danger" size="sm" onClick={() => handleDelete(sale.sale_id)} title={t('salesPage.actions.delete')} aria-label={t('salesPage.actions.delete')}>
                                                            <FaTrash />
                                                        </Button>
                                                    )}
                                                    {isPending && hasPermission(PERMISSIONS.CREATE_SALES) && (
                                                        <Button variant="success" size="sm" onClick={() => handleAddPaymentClick(sale)} title={t('salesPage.actions.addPayment')} aria-label={t('salesPage.actions.addPayment')}>
                                                            <FaPlus />
                                                        </Button>
                                                    )}
                                                    {cashStationsEnabled && isPendingCashier && hasPermission(PERMISSIONS.CREATE_SALES) && (
                                                        <Button variant="success" size="sm" onClick={() => { setSaleToCheckout(sale); setShowCheckoutModal(true); }} title={t('salesPage.actions.collect')} aria-label={t('salesPage.actions.collect')}>
                                                            <FaCashRegister />
                                                        </Button>
                                                    )}
                                                    {recurringEnabled && hasPermission(PERMISSIONS.CREATE_SALES) && (
                                                        <Button variant="outline-info" size="sm" onClick={() => handleRepeatSale(sale)} title={t('salesPage.recurring.repeatButton')} aria-label={t('salesPage.recurring.repeatButton')}>
                                                            <FaExchangeAlt />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )
                }
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                    <PageSizeSelector pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
                </div>
            </Container>

            {/* Sale Modal */}
            <Modal show={showModal} onHide={handleModalClose} size="lg" backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        {currentStep === 2 && (
                            <FaArrowLeft
                                onClick={() => setCurrentStep(1)}
                                style={{ cursor: 'pointer', marginRight: '10px', fontSize: '1.2rem' }}
                                title="Volver"
                            />
                        )}
                        {editingSale ? t('salesPage.saleModal.editTitle') : t('salesPage.saleModal.createTitle')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalError && <Alert variant="danger">{modalError}</Alert>}

                    {/* Sale Type Toggle - Only show in step 1 when creating new sale */}
                    {!editingSale && currentStep === 1 && (
                        <div className="mb-4">
                            <ButtonGroup className="w-100">
                                <Button
                                    variant={!isLayaway ? 'primary' : 'outline-primary'}
                                    onClick={() => setIsLayaway(false)}
                                    style={!isLayaway ? { backgroundColor: 'var(--ev-action-primary-bg)' } : {}}
                                >
                                    {t('salesPage.saleModal.directSale', 'Venta Directa')}
                                </Button>
                                <Button
                                    variant={isLayaway ? 'primary' : 'outline-primary'}
                                    onClick={() => setIsLayaway(true)}
                                    style={isLayaway ? { backgroundColor: 'var(--ev-action-primary-bg)' } : {}}
                                >
                                    {t('salesPage.saleModal.layawaySale', 'Venta por Separado')}
                                </Button>
                            </ButtonGroup>
                        </div>
                    )}

                    <Form onSubmit={handleModalSubmit}>
                        <fieldset disabled={editingSale && !hasPermission(PERMISSIONS.EDIT_SALES)}>
                            {currentStep === 1 && (
                                <>
                                    {/* Fechas */}
                                    <Row>
                                        <Col md={isLayaway ? 6 : 12}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('salesPage.saleModal.saleDate')}</Form.Label>
                                                <Form.Control type="datetime-local" name="sale_date" value={modalData.sale_date || ''} onChange={handleModalChange} />
                                            </Form.Group>
                                        </Col>
                                        {isLayaway && (
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('salesPage.saleModal.deliveryDateTime')}</Form.Label>
                                                    <Form.Control type="datetime-local" name="delivery_datetime" value={modalData.delivery_datetime || ''} onChange={handleModalChange} required />
                                                </Form.Group>
                                            </Col>
                                        )}
                                    </Row>
                                    <hr />

                                    {/* Productos */}
                                    <h5>{t('salesPage.saleModal.products')}</h5>
                                    {modalData.products?.map((item, index) => (
                                        <Row key={index} className="mb-3 align-items-end">
                                            <Col xs={12} md={5}>
                                                <Form.Label className="d-block">{t('salesPage.saleModal.productName')}</Form.Label>
                                                <CreatableSelect
                                                    isClearable
                                                    options={productOptions}
                                                    onChange={(option, action) => handleProductSelectChange(index, option, action)}
                                                    onCreateOption={(inputValue) => handleProductSelectChange(index, { value: inputValue, label: inputValue }, { action: 'create-option' })}
                                                    onInputChange={(inputValue) => handleProductInputChange(inputValue, index)}
                                                    onBlur={() => handleProductInputBlur(index)}
                                                    value={item.product_name ? { value: item.product_name, label: item.product_name } : null}
                                                    placeholder={t('salesPage.saleModal.searchProduct')}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={selectTruncateStyles}
                                                />
                                            </Col>
                                            <Col xs={4} md={2}>
                                                <Form.Label>{t('salesPage.saleModal.qty')}</Form.Label>
                                                <NumberInput
                                                    value={item.quantity || ''}
                                                    onChange={(value) => {
                                                        const e = { target: { name: 'quantity', value } };
                                                        handleProductItemChange(index, e);
                                                    }}
                                                    placeholder={t('salesPage.saleModal.qty')}
                                                    required
                                                />
                                            </Col>
                                            <Col xs={5} md={3}>
                                                <Form.Label>{t('salesPage.saleModal.unitPrice')}</Form.Label>
                                                <CurrencyInput
                                                    value={item.unit_price || ''}
                                                    onChange={(value) => {
                                                        const e = { target: { name: 'unit_price', value } };
                                                        handleProductItemChange(index, e);
                                                    }}
                                                    placeholder="0"
                                                    required
                                                />
                                            </Col>
                                            <Col xs={3} md={2} className="d-flex align-items-end">
                                                {(hasPermission(PERMISSIONS.CREATE_SALES) || (editingSale && hasPermission(PERMISSIONS.EDIT_SALES))) &&
                                                    <Button variant="outline-danger" size="sm" onClick={() => removeProductItem(index)}>{t('salesPage.saleModal.remove')}</Button>
                                                }
                                            </Col>

                                            <Col xs={12} className="px-3 pb-2">
                                                {(() => {
                                                    // Find product for stock check
                                                    const product = productList.find(p => p.name?.trim().toLowerCase() === item.product_name?.trim().toLowerCase());
                                                    if (!product) {
                                                        if (!editingSale && item.product_name?.trim() && hasPermission(PERMISSIONS.CREATE_PRODUCTS)) {
                                                            return (
                                                                <div className="mt-1 p-2 rounded d-flex align-items-center" style={{ backgroundColor: 'var(--ev-bg-brand-subtle)' }}>
                                                                    <Form.Check
                                                                        type="switch"
                                                                        id={`save-product-data-${index}`}
                                                                        label={
                                                                            <span className="fw-bold text-dark d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                                                                                <Badge bg="info" className="text-dark fw-normal">{t('salesPage.saleModal.newProductBadge', 'Producto nuevo')}</Badge>
                                                                                {t('salesPage.saleModal.saveProductData', 'Guardar producto en el sistema')}
                                                                            </span>
                                                                        }
                                                                        checked={!!item.save_to_catalog}
                                                                        onChange={(e) => handleToggleSaveProduct(index, e.target.checked)}
                                                                        className="mb-0 w-100"
                                                                        style={{ cursor: 'pointer' }}
                                                                    />
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    }

                                                    const available = parseFloat(product.available_quantity) || 0;
                                                    const minStock = parseFloat(product.minimum_stock) || 0;
                                                    const qty = parseFloat(item.quantity) || 0;
                                                    const isExceeding = qty > available;
                                                    const isLowStock = available <= minStock || (available - qty) <= minStock;

                                                    return (
                                                        <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
                                                            <span className="text-muted">
                                                                {t('salesPage.saleModal.availableStock', 'Disponible')}: <strong>{available}</strong>
                                                            </span>
                                                            {isExceeding && (
                                                                <span className="text-danger fw-bold d-flex align-items-center">
                                                                    <FaExclamationTriangle className="me-1" size={12} />
                                                                    {t('salesPage.saleModal.exceedsStock', 'Excede stock disponible')}
                                                                </span>
                                                            )}
                                                            {!isExceeding && isLowStock && (
                                                                <span className="text-warning fw-bold d-flex align-items-center">
                                                                    <FaExclamationTriangle className="me-1" size={12} />
                                                                    {t('salesPage.saleModal.lowStock', 'Stock bajo')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </Col>
                                        </Row>
                                    ))}
                                    {(hasPermission(PERMISSIONS.CREATE_SALES) || (editingSale && hasPermission(PERMISSIONS.EDIT_SALES))) && (
                                        <Button variant="outline-primary" size="sm" onClick={addProductItem} className="w-100 mb-3">
                                            <FaPlus className="me-1" /> {t('salesPage.saleModal.addProduct')}
                                        </Button>
                                    )}
                                    <div className="d-flex justify-content-end align-items-center mb-3">
                                        <h5 className="me-3 mb-0">{t('salesPage.saleModal.totalAmount')}</h5>
                                        <h4 className="fw-bold mb-0 text-primary">{formatCurrency(modalData.total_amount)}</h4>
                                    </div>




                                    {/* Detalles del Cliente */}
                                    <h5>{t('salesPage.saleModal.clientDetails')}</h5>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {t('salesPage.saleModal.searchClient')}
                                            {isLayaway && <span className="text-danger">*</span>}
                                        </Form.Label>
                                        <CreatableSelect isClearable options={clientOptions} value={selectedClientOption} onChange={handleClientSearch} placeholder={t('salesPage.saleModal.searchClient')} menuPortalTarget={document.body} menuPosition="fixed" styles={selectTruncateStyles} components={{ Input: MobilePasteInput }} />
                                    </Form.Group>
                                    {/* Toggle switch to save client data */}
                                    {!modalData.client_id && showClientDetails && (
                                        <div className="mb-3 p-3 rounded d-flex align-items-center" style={{ backgroundColor: 'var(--ev-bg-brand-subtle)' }}>
                                            <Form.Check
                                                type="switch"
                                                id="save-client-data"
                                                label={<span className="fw-bold text-dark" style={{ cursor: 'pointer' }}>{t('salesPage.saleModal.saveClientData', 'Guardar datos del cliente')}</span>}
                                                checked={saveClientData}
                                                onChange={(e) => setSaveClientData(e.target.checked)}
                                                className="mb-0 w-100"
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </div>
                                    )}
                                    {(modalData.client_id || showClientDetails) && (
                                        <>
                                            {modalData.client_id && (
                                                <Form.Text className="text-muted d-block mb-2">
                                                    {t('salesPage.saleModal.linkedClientHint', 'Cliente vinculado — para cambiar estos datos, edítalo desde Clientes.')}
                                                </Form.Text>
                                            )}
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            {t('salesPage.saleModal.clientName')}
                                                            {isLayaway && <span className="text-danger">*</span>}
                                                        </Form.Label>
                                                        <Form.Control type="text" name="client_name" value={modalData.client_name || ''} onChange={handleModalChange} readOnly={!!modalData.client_id} plaintext={!!modalData.client_id} />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            {t('salesPage.saleModal.clientPhone')}
                                                            {isLayaway && <span className="text-danger">*</span>}
                                                        </Form.Label>
                                                        <Row>
                                                            <Col xs={5} md={4}>
                                                                <PhoneInput
                                                                    value={modalData.client_phone_country_code}
                                                                    onChange={handleModalCountryCodeChange}
                                                                    numberInputComponent={ReadOnlyInput}
                                                                    className="phone-input-custom"
                                                                    defaultCountry="CO"
                                                                    international
                                                                    disabled={!!modalData.client_id}
                                                                />
                                                            </Col>
                                                            <Col xs={7} md={8}>
                                                                <Form.Control
                                                                    type="tel"
                                                                    name="client_phone"
                                                                    value={modalData.client_phone || ''}
                                                                    onChange={handleModalChange}
                                                                    onBlur={handlePhoneBlur}
                                                                    readOnly={!!modalData.client_id}
                                                                    plaintext={!!modalData.client_id}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('salesPage.saleModal.clientAddress')}</Form.Label>
                                                <Form.Control as="textarea" rows={2} name="client_address" value={modalData.client_address || ''} onChange={handleModalChange} readOnly={!!modalData.client_id} plaintext={!!modalData.client_id} />
                                            </Form.Group>
                                        </>
                                    )}
                                </>
                            )}

                            {currentStep === 2 && (
                                <>

                                    <PaymentMethodsSelector
                                        paymentMethods={paymentMethods}
                                        setPaymentMethods={setPaymentMethods}
                                        totalAmount={modalData.total_amount || 0}
                                        t={t}
                                        formatCurrency={formatCurrency}
                                        isLayaway={isLayaway}
                                        disabled={editingSale && hasMultiplePayments}
                                        disabledMessage={t('salesPage.paymentMethods.disabledDueToPayments', 'No se pueden editar los pagos porque ya existen abonos registrados.')}
                                    />
                                    <hr className="my-3" />
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('salesPage.saleModal.observations')}</Form.Label>
                                        <Form.Control as="textarea" rows={2} name="observations" value={modalData.observations || ''} onChange={handleModalChange} />
                                    </Form.Group>
                                    {/* Recurring template checkbox */}
                                    {recurringEnabled && (
                                        <div className="p-2 rounded d-flex align-items-center" style={{ backgroundColor: 'var(--ev-bg-subtle)' }}>
                                            <Form.Check
                                                type="checkbox"
                                                id="recurring-template-check"
                                                label={<span className="text-muted small">{t('salesPage.recurring.saveAsTemplate', 'Guardar como venta recurrente')}</span>}
                                                checked={isRecurringTemplate}
                                                onChange={(e) => setIsRecurringTemplate(e.target.checked)}
                                                className="mb-0"
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </fieldset>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>{t('salesPage.saleModal.close')}</Button>
                    {currentStep === 1 && (
                        <Button
                            variant="primary"
                            onClick={() => setCurrentStep(2)}
                            disabled={
                                !modalData.products?.every(p => p.product_name?.trim() && p.unit_price && parseFloat(p.unit_price) > 0) ||
                                (isLayaway && !modalData.client_name?.trim() && !modalData.client_phone?.trim())
                            }
                        >
                            {t('salesPage.saleModal.next', 'Siguiente')}
                        </Button>
                    )}
                    {currentStep === 2 && (
                        <>
                            {((editingSale && hasPermission(PERMISSIONS.EDIT_SALES)) || (!editingSale && hasPermission(PERMISSIONS.CREATE_SALES))) &&
                                <Button
                                    variant="primary"
                                    onClick={handleModalSubmit}
                                    disabled={!isLayaway && paymentMethods.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) < (modalData.total_amount || 0) - 0.01}
                                >
                                    {editingSale
                                        ? t('salesPage.saleModal.updateSale')
                                        : (cashStationsEnabled && !isCashRegisterOpen && openContext?.count > 1)
                                            ? t('salesPage.createOrder', 'Crear Pedido')
                                            : t('salesPage.saleModal.saveSale')}
                                </Button>
                            }
                        </>
                    )}
                </Modal.Footer>
            </Modal >

            {/* Share Modal */}
            < ShareModal
                show={showShareModal}
                onHide={() => setShowShareModal(false)}
                shareableLink={shareableLink}
                message={saleToShare ? `${t('shareModal.whatsappMessagePrefix')} ${saleToShare.client_name}! ${t('shareModal.whatsappMessageInvoiceNum')} #${saleToShare.invoice_number} ${t('shareModal.whatsappMessageTotal')} ${formatCurrency(saleToShare.total_amount)}. ${t('shareModal.whatsappMessageLink')}: ${shareableLink}` : ''}
                title={t('shareModal.title')}
                phoneNumber={saleToShare ? (() => {
                    const countryCode = saleToShare.client_phone_country_code ? saleToShare.client_phone_country_code.replace(/\D/g, '') : '';
                    const phoneNumber = saleToShare.client_phone ? saleToShare.client_phone.replace(/\D/g, '') : '';
                    const fullPhone = countryCode + phoneNumber;
                    return fullPhone || null;
                })() : null}
                onPrint={() => handlePrintClick(saleToShare)}
                onPrintDeliverySlip={() => handleDeliverySlipPrint(saleToShare)}
                openButtonLabel={t('shareModal.openInvoice')}
                receiptSale={receiptForDownload}
                receiptFileName={saleToShare ? `recibo-${saleToShare.invoice_number}` : 'recibo'}
            />

            {/* Add Payment Modal */}
            {
                saleForPayment && (
                    <Modal show={showPaymentModal} onHide={handlePaymentModalClose} centered backdrop={isMobile ? 'static' : true}>
                        <Modal.Header closeButton>
                            <Modal.Title>{t('salesPage.paymentModal.title', { invoiceNumber: saleForPayment.sale_id })}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {paymentError && <Alert variant="danger">{paymentError}</Alert>}

                            {/* Installment Limit Warnings */}
                            {isInstallmentLimitReached && (
                                <div className="mb-3 p-3 rounded shadow-sm text-white d-flex align-items-center justify-content-between"
                                    style={{ background: 'linear-gradient(90deg, #EF4444 0%, #DC2626 100%)' }}>
                                    <div className="d-flex align-items-center gap-3">
                                        <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', padding: '0.5rem', borderRadius: '50%', display: 'flex' }}>
                                            <FaExclamationTriangle size={20} />
                                        </div>
                                        <div>
                                            <h6 className="mb-1 fw-bold">{t('salesPage.paymentModal.limitReachedTitle')}</h6>
                                            <p className="mb-0 small text-white-50" style={{ lineHeight: '1.2' }}>
                                                {t('salesPage.paymentModal.limitReachedMessage', { max: maxInstallments })}
                                            </p>
                                        </div>
                                    </div>
                                    <Button variant="light" size="sm" href="/settings/plan" className="text-danger fw-bold ms-3 text-nowrap" style={{ fontSize: '0.8rem' }}>
                                        {t('salesPage.paymentModal.upgradePlan')}
                                    </Button>
                                </div>
                            )}

                            {isLastInstallment && (
                                <div className="mb-3 p-3 rounded shadow-sm text-white"
                                    style={{ background: 'linear-gradient(90deg, #F59E0B 0%, #D97706 100%)' }}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="d-flex align-items-center gap-3">
                                            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', padding: '0.5rem', borderRadius: '50%', display: 'flex' }}>
                                                <FaInfoCircle size={20} />
                                            </div>
                                            <div>
                                                <h6 className="mb-1 fw-bold">{t('salesPage.paymentModal.lastInstallmentTitle')}</h6>
                                                <p className="mb-0 small text-white-50" style={{ lineHeight: '1.2' }}>
                                                    {t('salesPage.paymentModal.lastInstallmentMessage')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between ps-5">
                                        <p className="mb-0 small text-white-50 fst-italic">
                                            {t('salesPage.paymentModal.needMoreInstallments')}
                                        </p>
                                        <Button variant="light" size="sm" href="/settings/plan" className="text-warning fw-bold ms-3 text-nowrap" style={{ fontSize: '0.8rem', color: '#D97706' }}>
                                            {t('salesPage.paymentModal.upgradePlan')}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <h5>{t('salesPage.paymentModal.summary')}</h5>
                            {maxInstallments !== -1 && (
                                <p className="text-muted mb-2">
                                    {t('salesPage.paymentModal.installmentIndicator', { current: currentInstallmentNumber, max: maxInstallments })}
                                </p>
                            )}

                            <p><strong>{t('salesPage.paymentModal.client')}</strong> {saleForPayment.client_name}</p>
                            <div className="modern-table-wrapper">
                                <Table className="modern-table modern-table-sm">
                                    <tbody>
                                        <tr className="table-danger">
                                            <td><strong>{t('salesPage.paymentModal.remainingBalance')}</strong></td>
                                            <td className="text-end">
                                                <strong>{formatCurrency(parseFloat(saleForPayment.total_amount) - parseFloat(saleForPayment.paid_amount))}</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                            <hr />
                            {!isInstallmentLimitReached && (
                                <Form onSubmit={(e) => { e.preventDefault(); handlePaymentSubmit(); }}>
                                    <fieldset disabled={!hasPermission(PERMISSIONS.CREATE_SALES)}>
                                        <PaymentMethodsSelector
                                            paymentMethods={addPaymentMethods}
                                            setPaymentMethods={setAddPaymentMethods}
                                            totalAmount={parseFloat(saleForPayment.total_amount) - parseFloat(saleForPayment.paid_amount)}
                                            t={t}
                                            isCashReceivedEnabled={true}
                                            formatCurrency={formatCurrency}
                                        />
                                        <Form.Group className="mb-3 mt-3">
                                            <Form.Label>{t('salesPage.paymentModal.observations')}</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={paymentObservations}
                                                onChange={(e) => setPaymentObservations(e.target.value)}
                                            />
                                        </Form.Group>
                                    </fieldset>
                                </Form>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handlePaymentModalClose}>{t('salesPage.paymentModal.close')}</Button>
                            {hasPermission(PERMISSIONS.CREATE_SALES) && !isInstallmentLimitReached && (
                                <Button variant="primary" onClick={handlePaymentSubmit} disabled={!isPaymentValid}>
                                    {t('salesPage.paymentModal.savePayment')}
                                </Button>
                            )}
                        </Modal.Footer>
                    </Modal>
                )
            }

            {/* Expense Modal */}
            <CreateExpenseModal
                show={showExpenseModal}
                onHide={() => setShowExpenseModal(false)}
                suppliers={suppliers}
                onExpenseCreated={() => fetchSales()}
                isCashRegisterOpen={isCashRegisterOpen || !canOpenAStation}
                onOpenCashRegister={(options) => {
                    // Use the hook to handle pending expense callback
                    if (options?.pendingCallback) {
                        setPendingTransactionCallback(options.pendingCallback);
                    }
                    setShowExpenseModal(false);
                }}
            />

            {/* Client Update Confirmation Modal */}
            <Modal show={showUpdateConfirm} onHide={() => setShowUpdateConfirm(false)} centered backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('salesPage.saleModal.existingClient', 'Cliente Existente')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('salesPage.saleModal.existingClientMessage', 'Ya existe un cliente registrado con este teléfono:')}</p>
                    <div className="bg-light p-3 rounded mb-3">
                        <p className="mb-1"><strong>Nombre:</strong> {existingClientData?.name}</p>
                        <p className="mb-1"><strong>Teléfono:</strong> {existingClientData?.phone_country_code} {existingClientData?.phone}</p>
                        <p className="mb-0"><strong>Dirección:</strong> {existingClientData?.address || 'No registrada'}</p>
                    </div>
                    <p>{t('salesPage.saleModal.updateClientQuestion', '¿Deseas actualizar los datos del cliente con la nueva información?')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={handleContinueWithoutUpdate}>
                        {t('salesPage.saleModal.continueWithoutUpdate', 'Continuar sin actualizar')}
                    </Button>
                    <Button variant="primary" onClick={handleUpdateClient}>
                        {t('salesPage.saleModal.updateClient', 'Actualizar Datos')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Sale Detail Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('salesPage.detailModal.title', 'Detalle del Pedido')}
                        {detailSale && <span className="ms-2 text-muted fs-6">#{detailSale.invoice_number}</span>}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : detailSale ? (
                        <>
                            {/* Status + amounts */}
                            <div className="d-flex gap-3 flex-wrap mb-3">
                                <div className="flex-fill bg-light rounded p-3 text-center">
                                    <div className="text-muted small mb-1">{t('salesPage.table.total')}</div>
                                    <div className="fw-bold fs-5">{formatCurrency(detailSale.total_amount)}</div>
                                </div>
                                <div className="flex-fill bg-light rounded p-3 text-center">
                                    <div className="text-muted small mb-1">{t('salesPage.table.paid')}</div>
                                    <div className="fw-bold fs-5 text-success">{formatCurrency(detailSale.paid_amount)}</div>
                                </div>
                                <div className="flex-fill bg-light rounded p-3 text-center">
                                    <div className="text-muted small mb-1">{t('salesPage.table.remaining')}</div>
                                    <div className={`fw-bold fs-5 ${parseFloat(detailSale.total_amount) - parseFloat(detailSale.paid_amount) > 0 ? 'text-danger' : 'text-success'}`}>
                                        {formatCurrency(parseFloat(detailSale.total_amount) - parseFloat(detailSale.paid_amount))}
                                    </div>
                                </div>
                            </div>

                            {/* Client */}
                            {(detailSale.client_name || detailSale.client_phone) && (
                                <Row className="mb-3">
                                    <Col sm={6}>
                                        <div className="text-muted small">{t('salesPage.detailModal.client')}</div>
                                        <div className="fw-semibold">{detailSale.client_name || '—'}</div>
                                    </Col>
                                    <Col sm={6}>
                                        <div className="text-muted small">{t('salesPage.detailModal.phone')}</div>
                                        <div className="fw-semibold">
                                            {detailSale.client_phone ? `${detailSale.client_phone_country_code || ''} ${detailSale.client_phone}` : '—'}
                                        </div>
                                    </Col>
                                    {detailSale.client_address && (
                                        <Col sm={12} className="mt-2">
                                            <div className="text-muted small">{t('salesPage.saleModal.clientAddress')}</div>
                                            <div className="fw-semibold">{detailSale.client_address}</div>
                                        </Col>
                                    )}
                                </Row>
                            )}

                            {/* Seller + Register */}
                            <Row className="mb-3">
                                <Col sm={6}>
                                    <div className="text-muted small">{t('salesPage.detailModal.seller', 'Vendedor')}</div>
                                    <div className="fw-semibold">{detailSale.seller_name || t('salesPage.detailModal.noSeller', 'Sin vendedor registrado')}</div>
                                </Col>
                                <Col sm={6}>
                                    <div className="text-muted small">{t('salesPage.detailModal.status', 'Estado')}</div>
                                    <span className={`badge bg-${detailSale.status === 'PENDING_CASHIER' ? 'info' : detailSale.status === 'PENDING_PAYMENT' ? 'warning' : 'success'}`}>
                                        {t(`salesPage.statuses.${detailSale.status}`, detailSale.status)}
                                    </span>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col sm={6}>
                                    <div className="text-muted small">{t('salesPage.table.saleDate')}</div>
                                    <div className="fw-semibold">{new Date(detailSale.sale_date || detailSale.created_at).toLocaleString()}</div>
                                </Col>
                                <Col sm={6}>
                                    <div className="text-muted small">{t('salesPage.table.deliveryDate')}</div>
                                    <div className="fw-semibold">{new Date(detailSale.delivery_datetime).toLocaleString()}</div>
                                </Col>
                            </Row>

                            {/* Products */}
                            {detailSale.products?.length > 0 && (
                                <>
                                    <hr />
                                    <h6 className="mb-2">{t('salesPage.detailModal.products', 'Productos')}</h6>
                                    <Table size="sm" bordered className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t('salesPage.saleModal.productName')}</th>
                                                <th className="text-center">{t('salesPage.saleModal.qty')}</th>
                                                <th className="text-end">{t('salesPage.saleModal.unitPrice')}</th>
                                                <th className="text-end">{t('salesPage.table.total')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detailSale.products.map((item, i) => (
                                                <tr key={i}>
                                                    <td>{item.product_name}</td>
                                                    <td className="text-center">{item.quantity}</td>
                                                    <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                    <td className="text-end">{formatCurrency(parseFloat(item.unit_price) * parseFloat(item.quantity))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </>
                            )}

                            {/* Payment history */}
                            <hr />
                            <h6 className="mb-2">{t('salesPage.detailModal.payments', 'Historial de Pagos')}</h6>
                            {detailSale.payments?.length > 0 ? (
                                <Table size="sm" bordered className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t('salesPage.detailModal.paymentDate', 'Fecha')}</th>
                                            <th className="text-end">{t('salesPage.detailModal.paymentAmount', 'Monto')}</th>
                                            <th>{t('salesPage.detailModal.paymentMethod', 'Método')}</th>
                                            <th>{t('salesPage.detailModal.cashier', 'Cajero')}</th>
                                            <th>{t('salesPage.detailModal.station', 'Estación')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailSale.payments.map((p, i) => (
                                            <tr key={i}>
                                                <td>{new Date(p.payment_date).toLocaleString()}</td>
                                                <td className="text-end">{formatCurrency(p.amount)}</td>
                                                <td>{t(`paymentMethods.${p.payment_method}`, p.payment_method)}</td>
                                                <td>{p.cashier_name || '—'}</td>
                                                <td>{p.station_name || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted small mb-0">{t('salesPage.detailModal.noPayments', 'Sin pagos registrados.')}</p>
                            )}
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    {detailSale && recurringEnabled && (
                        <Button variant="outline-primary" className="me-auto" onClick={() => handleRepeatSale(detailSale)}>
                            <FaPlus className="me-1" />{t('salesPage.recurring.repeatButton', 'Repetir venta')}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        {t('common.close', 'Cerrar')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Open Cash Register Modal - using hook props */}
            <OpenCashRegisterModal {...cashModalProps} />

            {/* Close Cash Register Modal */}
            <CloseCashRegisterModal
                show={showCloseCashModal}
                onHide={() => setShowCloseCashModal(false)}
                cashRegister={cashRegister}
                onSuccess={() => {
                    refetchCashStatus();
                    // Toast is handled inside the modal
                }}
            />

            {/* Cashier Checkout Modal - collect a PENDING_CASHIER order */}
            <CheckoutModal
                show={showCheckoutModal}
                sale={saleToCheckout}
                onHide={() => setShowCheckoutModal(false)}
                onSuccess={() => {
                    fetchSales();
                    refetchCashStatus();
                }}
            />
        </>
    );
};

export default SalesPage;
